import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import { Field, Skeleton } from '../components/ui';
import { api, type Asset, type Customer } from '../lib/api';
import { useAction, useLoad } from '../lib/hooks';
import { INTERVAL_WEEKS, intervalWarning } from '../../../shared/schedule';
import './screens.css';

interface FormState {
  kind: 'vehicle' | 'trailer';
  customerId: string;
  regNo: string;
  fleetNo: string;
  vin: string;
  chassisNo: string;
  make: string;
  model: string;
  bodyType: string;
  axles: string;
  firstUseDate: string;
  inspectionIntervalWeeks: string;
  lastInspectionDate: string;
  odometer: string;
  odometerUnit: string;
  motExpiry: string;
  tachoCalibrationExpiry: string;
  platingExpiry: string;
  lolerExpiry: string;
  taxExpiry: string;
  insuranceExpiry: string;
  notes: string;
}

const EMPTY: FormState = {
  kind: 'vehicle',
  customerId: '',
  regNo: '',
  fleetNo: '',
  vin: '',
  chassisNo: '',
  make: '',
  model: '',
  bodyType: '',
  axles: '3',
  firstUseDate: '',
  inspectionIntervalWeeks: '6',
  lastInspectionDate: '',
  odometer: '',
  odometerUnit: 'miles',
  motExpiry: '',
  tachoCalibrationExpiry: '',
  platingExpiry: '',
  lolerExpiry: '',
  taxExpiry: '',
  insuranceExpiry: '',
  notes: '',
};

export default function AssetForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const { busy, error, run } = useAction();
  const [form, setForm] = useState<FormState>(EMPTY);

  const { data: customerData } = useLoad<{ customers: Customer[] }>(() => api.get('/customers'));
  const { data: assetData, loading } = useLoad<{ asset: Asset } | null>(
    () => (editing ? api.get(`/assets/${id}`) : Promise.resolve(null)),
    [id],
  );

  useEffect(() => {
    const asset = assetData?.asset;
    if (!asset) return;
    setForm({
      kind: asset.kind,
      customerId: asset.customer_id ?? '',
      regNo: asset.reg_no ?? '',
      fleetNo: asset.fleet_no ?? '',
      vin: asset.vin ?? '',
      chassisNo: asset.chassis_no ?? '',
      make: asset.make ?? '',
      model: asset.model ?? '',
      bodyType: asset.body_type ?? '',
      axles: String(asset.axles ?? 3),
      firstUseDate: asset.first_use_date ?? '',
      inspectionIntervalWeeks: String(asset.inspection_interval_weeks ?? 6),
      lastInspectionDate: asset.last_inspection_date ?? '',
      odometer: asset.odometer != null ? String(asset.odometer) : '',
      odometerUnit: asset.odometer_unit ?? 'miles',
      motExpiry: asset.mot_expiry ?? '',
      tachoCalibrationExpiry: asset.tacho_calibration_expiry ?? '',
      platingExpiry: asset.plating_expiry ?? '',
      lolerExpiry: asset.loler_expiry ?? '',
      taxExpiry: asset.tax_expiry ?? '',
      insuranceExpiry: asset.insurance_expiry ?? '',
      notes: asset.notes ?? '',
    });
  }, [assetData]);

  const set =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const isTrailer = form.kind === 'trailer';

  const warning = intervalWarning(
    form.firstUseDate || null,
    Number(form.inspectionIntervalWeeks) || 6,
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      const payload = {
        ...form,
        axles: Number(form.axles) || 2,
        inspectionIntervalWeeks: Number(form.inspectionIntervalWeeks) || 6,
        odometer: form.odometer === '' ? null : Number(form.odometer),
        customerId: form.customerId || null,
      };
      const result = editing
        ? await api.patch<{ asset: Asset }>(`/assets/${id}`, payload)
        : await api.post<{ asset: Asset }>('/assets', payload);
      navigate(`/fleet/${result.asset.id}`, { replace: true });
    });
  };

  if (editing && loading) {
    return (
      <Screen title="Edit vehicle" back={`/fleet/${id}`}>
        <Skeleton variant="detail" rows={4} />
      </Screen>
    );
  }

  return (
    <Screen
      title={editing ? 'Edit vehicle' : 'Add to fleet'}
      back={editing ? `/fleet/${id}` : '/fleet'}
      hideTabs
    >
      <form className="stack-5" onSubmit={submit}>
        {error ? (
          <div className="notice notice-danger" role="alert">
            {error}
          </div>
        ) : null}

        <fieldset className="stack-4 fieldset">
          <legend className="eyebrow">Identity</legend>

          <div className="segmented" role="radiogroup" aria-label="Type">
            {(['vehicle', 'trailer'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                role="radio"
                aria-checked={form.kind === kind}
                className={`segment${form.kind === kind ? ' is-on' : ''}`}
                onClick={() => setForm((f) => ({ ...f, kind }))}
              >
                {kind === 'vehicle' ? 'Powered vehicle' : 'Trailer'}
              </button>
            ))}
          </div>
          <p className="hint">
            {isTrailer
              ? 'Trailers use the RHA VEH002 form — 61 items.'
              : 'Powered vehicles use the RHA VEH001 form — 73 items.'}
          </p>

          <div className="grid-2 collapse">
            <Field label={isTrailer ? 'Registration (if any)' : 'Registration'}>
              <input
                className="input input-mono"
                value={form.regNo}
                onChange={set('regNo')}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                placeholder="YX21 KHM"
                required={!isTrailer}
              />
            </Field>
            <Field label="Fleet number">
              <input className="input" value={form.fleetNo} onChange={set('fleetNo')} placeholder="104" />
            </Field>
          </div>

          <Field label="Customer">
            <select className="select" value={form.customerId} onChange={set('customerId')}>
              <option value="">— No customer —</option>
              {(customerData?.customers ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid-2 collapse">
            <Field label="Make">
              <input className="input" value={form.make} onChange={set('make')} placeholder="DAF" />
            </Field>
            <Field label="Model">
              <input className="input" value={form.model} onChange={set('model')} placeholder="XF 480" />
            </Field>
          </div>

          {isTrailer ? (
            <div className="grid-2 collapse">
              <Field label="Body type">
                <input className="input" value={form.bodyType} onChange={set('bodyType')} placeholder="Curtainsider" />
              </Field>
              <Field label="Chassis number">
                <input className="input input-mono" value={form.chassisNo} onChange={set('chassisNo')} />
              </Field>
            </div>
          ) : null}

          <div className="grid-2 collapse">
            <Field label="VIN">
              <input
                className="input input-mono"
                value={form.vin}
                onChange={set('vin')}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={17}
              />
            </Field>
            <Field label="Axles">
              <select className="select" value={form.axles} onChange={set('axles')}>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </fieldset>

        <fieldset className="stack-4 fieldset">
          <legend className="eyebrow">Inspection schedule</legend>

          <Field label="Inspection interval">
            <div className="interval-grid" role="radiogroup" aria-label="Inspection interval">
              {INTERVAL_WEEKS.map((weeks) => (
                <button
                  key={weeks}
                  type="button"
                  role="radio"
                  aria-checked={Number(form.inspectionIntervalWeeks) === weeks}
                  className={`interval${Number(form.inspectionIntervalWeeks) === weeks ? ' is-on' : ''}`}
                  onClick={() => setForm((f) => ({ ...f, inspectionIntervalWeeks: String(weeks) }))}
                >
                  <span className="interval-num">{weeks}</span>
                  <span className="interval-unit">weeks</span>
                </button>
              ))}
            </div>
          </Field>

          {warning ? (
            <div className="notice notice-warn" role="status">
              {warning}
            </div>
          ) : null}

          <div className="grid-2 collapse">
            <Field label="Last inspection" hint="The next due date follows from this.">
              <input className="input" type="date" value={form.lastInspectionDate} onChange={set('lastInspectionDate')} />
            </Field>
            <Field label="First used">
              <input className="input" type="date" value={form.firstUseDate} onChange={set('firstUseDate')} />
            </Field>
          </div>

          <div className="grid-2 collapse">
            <Field label={isTrailer ? 'Hubometer' : 'Odometer'}>
              <input
                className="input"
                type="number"
                inputMode="numeric"
                value={form.odometer}
                onChange={set('odometer')}
              />
            </Field>
            <Field label="Units">
              <select className="select" value={form.odometerUnit} onChange={set('odometerUnit')}>
                <option value="miles">Miles</option>
                <option value="km">Kilometres</option>
                <option value="hubo">Hubometer</option>
              </select>
            </Field>
          </div>
        </fieldset>

        <fieldset className="stack-4 fieldset">
          <legend className="eyebrow">Renewal dates</legend>
          <p className="hint">Leave blank if it does not apply. Anything dated here appears in reminders.</p>

          <div className="grid-2 collapse">
            <Field label={isTrailer ? 'Annual test' : 'MOT / annual test'}>
              <input className="input" type="date" value={form.motExpiry} onChange={set('motExpiry')} />
            </Field>
            <Field label="Plating certificate">
              <input className="input" type="date" value={form.platingExpiry} onChange={set('platingExpiry')} />
            </Field>
          </div>

          {!isTrailer ? (
            <div className="grid-2 collapse">
              <Field label="Tachograph calibration" hint="Two-yearly.">
                <input
                  className="input"
                  type="date"
                  value={form.tachoCalibrationExpiry}
                  onChange={set('tachoCalibrationExpiry')}
                />
              </Field>
              <Field label="Vehicle tax">
                <input className="input" type="date" value={form.taxExpiry} onChange={set('taxExpiry')} />
              </Field>
            </div>
          ) : null}

          <div className="grid-2 collapse">
            <Field label="LOLER" hint="Tail lift or crane.">
              <input className="input" type="date" value={form.lolerExpiry} onChange={set('lolerExpiry')} />
            </Field>
            <Field label="Insurance">
              <input className="input" type="date" value={form.insuranceExpiry} onChange={set('insuranceExpiry')} />
            </Field>
          </div>
        </fieldset>

        <Field label="Notes">
          <textarea className="textarea" value={form.notes} onChange={set('notes')} rows={3} />
        </Field>

        <div className="actionbar">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate(editing ? `/fleet/${id}` : '/fleet')}
          >
            Cancel
          </button>
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Add to fleet'}
          </button>
        </div>
      </form>
    </Screen>
  );
}
